import React, { useEffect } from 'react';
import { Card, Empty, Select, Space, Row, Col } from 'antd';
import styled from 'styled-components';
import ProjectSelection from './ProjectSelection';
import UnitSelection from './UnitSelection';
import {
  CreateUpdateDealUnitCommand,
  DealPricingMode,
  DealStage,
  DealBasicDetailsDto,
  DealUnitDto,
  DealPaymentsDto
} from 'api/models';
import { useTranslation } from 'react-i18next';
import { formatSquareMeters } from 'utility/utility';
import { formatCurrency } from 'utility/utility';
import { CenteredWrapper } from 'container/styled';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProjectColumn = styled(Col)`
  border-right: 1px solid #f0f0f0;
  height: 100%;
  min-height: 400px;
  overflow-y: auto;
`;

const SummaryCard = styled(Card)`
  margin-top: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryItemDetails = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

const GrandTotal = styled.div`
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const GrandTotalAmount = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1890ff;
`;

const UnitTitle = styled.div`
  font-weight: 500;
`;

// Updated interface to work with split data structure
interface DealData {
  basic?: DealBasicDetailsDto;
  units?: DealUnitDto[];
  payments?: DealPaymentsDto;
}

interface Props {
  value?: {
    projectId?: number;
    units?: CreateUpdateDealUnitCommand[];
    stage?: DealStage;
    pricingMode?: DealPricingMode;
  };
  editMode?: boolean;
  currentDealId?: number;
  deal?: DealData; // Updated to use the new split structure
  onChange: (value: any) => void;
}

const ProjectAndUnitSelection: React.FC<Props> = ({ value, onChange, editMode, currentDealId, deal }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!editMode && (!value || value.stage === undefined)) {
      onChange({
        ...value,
        stage: DealStage.InProgress
      });
    }
  }, [value, onChange, editMode]);

  const handleStageChange = (stage: DealStage) => {
    onChange({ ...value, stage });
  };

  const handleProjectChange = (projectId: number) => {
    onChange({ ...value, projectId, units: [] });
  };

  const handleUnitsChange = (units: any[]) => {
    onChange({ ...value, units });
  };

  const stageOptions = [
    { value: DealStage.InProgress, label: t('deal-stage:in-progress', 'In Progress') },
    { value: DealStage.ClosedWon, label: t('deal-stage:closed-won', 'Closed (Won)') },
  ];

  const handlePricingModeChange = (mode: DealPricingMode) => {
    onChange({ ...value, pricingMode: mode });
  };

  return (
    <Container>
      {!editMode && (
        <Card title={t('create-deal:select-deal-stage', 'Select Deal Stage')}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              {t('create-deal:deal-stage', 'Deal Stage')}{' '}
              <span style={{ color: '#ff4d4f', marginRight: '10px' }}>*</span>
              <Select
                style={{ width: '15%' }}
                value={value?.stage ?? DealStage.InProgress}
                onChange={handleStageChange}
                options={stageOptions}
                placeholder={t('create-deal:select-deal-stage', 'Select Deal Stage')}
              />
            </div>
          </Space>
        </Card>
      )}

      <Card title={t('create-deal:select-project-units', 'Select Project and Units')}>
        <Row gutter={24} style={{ minHeight: '400px' }}>
          <ProjectColumn span={6}>
            <ProjectSelection value={value?.projectId} onChange={handleProjectChange} />
          </ProjectColumn>
          <Col span={18}>
            {value?.projectId ? (
              <UnitSelection
                projectId={value?.projectId}
                editMode={editMode}
                currentDealId={currentDealId}
                deal={deal}
                value={value?.units}
                onChange={handleUnitsChange}
                defaultCollapsed={true}
                pricingMode={editMode ? deal?.basic?.pricingMode : value?.pricingMode}
                onPricingModeChange={handlePricingModeChange}
              />
            ) : (
              <CenteredWrapper>
                <Empty description={t('create-deal:select-project', 'Please select a project first')} />
              </CenteredWrapper>
            )}
          </Col>
        </Row>
      </Card>

      {value?.units?.length! > 0 && (
        <SummaryCard title={t('create-deal:selected-units', 'Selected Units')}>
          {value?.units?.filter(x => (x.quantity || 0) > 0).map(unit => {
            const unitDetails = unit;
            const quantity = unit.floorAllocations
              ? unit.floorAllocations.reduce((sum: number, a: any) => sum + (a.stock || 0), 0)
              : unit.quantity!;

            const totalPrice =
              value?.pricingMode === DealPricingMode.NUMBER_1
                ? unit.totalPrice!
                : quantity * unit.negotiatedPricePerSqm! * unitDetails!.squareMeters!;

            return (
              <SummaryItem key={unit.unitId}>
                <div>
                  <UnitTitle>{unitDetails?.name}</UnitTitle>
                  <SummaryItemDetails>
                    {value?.pricingMode === DealPricingMode.NUMBER_1
                      ? // Total price mode
                      `${quantity}•(${formatSquareMeters(unitDetails?.squareMeters!)}) ${t(
                        'deals:with-total-price-of',
                        'with total price of',
                      )} ${formatCurrency(unit.totalPrice!)}`
                      : // Price per m² mode
                      `${quantity} × ${formatSquareMeters(unitDetails?.squareMeters!)} × ${formatCurrency(
                        unit.negotiatedPricePerSqm,
                      )}/m²`}
                  </SummaryItemDetails>
                </div>
                <span>{formatCurrency(totalPrice)}</span>
              </SummaryItem>
            );
          })}

          <GrandTotal>
            <TotalRow>
              <span>{t('create-deal:total-amount', 'Total Amount')}</span>
              <GrandTotalAmount>
                {formatCurrency(
                  value?.units?.reduce((total, unit) => {
                    const unitDetails = unit;
                    const quantity = unit.floorAllocations
                      ? unit.floorAllocations.reduce((sum: number, a: any) => sum + (a.stock || 0), 0)
                      : unit.quantity!;

                    return (
                      total +
                      (value?.pricingMode === DealPricingMode.NUMBER_1
                        ? unit.totalPrice!
                        : quantity * unit.negotiatedPricePerSqm! * unitDetails!.squareMeters!)
                    );
                  }, 0),
                )}
              </GrandTotalAmount>
            </TotalRow>
          </GrandTotal>
        </SummaryCard>
      )}
    </Container>
  );
};

export default ProjectAndUnitSelection;